import React, { useState, useCallback, useMemo, useEffect } from "react";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  GridReadyEvent,
  ColDef,
  GridOptions,
} from "ag-grid-community";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import useCustomStyles from "../hooks/CustomStylesHook";
import {
  editResponse,
  fetchUserQueries,
  sendEmail,
} from "../services/services";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import CustomHeader from "./CustomHeaderRenderer";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/Store";
import { addEmail, EmailData } from "../redux/actions";
import {
  EMAILS_CONSTANTS,
  EXPLORE_FILES,
  SnackbarInterface,
} from "../constants/constants";
import { EmailActionCellRenderer } from "./ActionCellRenderer";

interface EmailsData {
  email: string;
  mobile_num: string;
  subject: string;
  query: string;
  response: string;
  course_name: "",
}

const styles = (theme: any) => ({
  container: {
    padding: "20px",
    minHeight: "90vh",
    justifyContent: "center",
    alignItems: "center",
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 100%)`,
  },
  titleContainer: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "10px 0px",
  },
  title: {
    fontSize: "28px",
    fontWeight: "bold",
    color: "#ffffff",
    marginBottom: "20px",
    textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)",
  },
  agGridContainer: {
    height: "70vh",
    width: "100%",
    backgroundColor: "white",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    border: "none",
    "& .ag-header": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
    "& .ag-header-cell": {
      fontWeight: "bold",
      fontSize: "16px",
      textAlign: "center",
      padding: "10px",
      color: "white",
    },
    "& .ag-cell": {
      textAlign: "center",
    },
    "& .ag-cell-label-container": {
      textAlign: "center",
      padding: "0px",
    },
    "& .ag-row-even": {
      backgroundColor: theme.palette.background.default,
    },
    "& .ag-row-odd": {
      backgroundColor: theme.palette.background.paper,
    },
    "& .ag-row:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .ag-row-selected": {
      backgroundColor: theme.palette.action.selected,
    },
    "& .ag-icon, .ag-filter-icon": {
      color: theme.palette.primary.main,
    },
    "& .ag-paging-panel": {
      backgroundColor: theme.palette.background.default,
      padding: "10px",
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    "& .ag-header-cell-resize": {
      backgroundColor: theme.palette.background.default,
    },
  },
  pagination: {
    color: "white",
    "& .MuiIconButton-root": { color: "white" },
    "& .MuiSvgIcon-root-MuiSelect-icon": {
      color: "white",
    },
    "& .MuiSelect-icon": { color: "white" },
  },
  sendButton: {
    backgroundColor: theme.palette.success.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  dialogContent: {
    padding: "20px",
  },
  textField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
    },
  },
  loader: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    zIndex: 9998,
  },
  editDialog: {
    "& .MuiDialog-paper": {
      width: "600px",
      maxWidth: "90vw",
    },
  },
  editDialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "24px",
  },
  dialogTextField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: theme.palette.background.paper,
    },
  },
  actionButtons: {
    display: "flex",
    gap: "10px",
    justifyContent: "center",
  },
  editButton: {
    minWidth: "40px",
    padding: "6px",
    marginRight: "8px",
  },
  searchField: {
    width: "300px",
    backgroundColor: theme.palette.background.default,
    borderRadius: "5px",
    marginBottom: "10px",
    "& .MuiInputBase-root": {
      height: "40px",
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "5px",
    },
  },
});

const EmailGrid: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const classes = useCustomStyles(styles, theme);
  const emails = useSelector((state: RootState) => state.fileReducer?.emails);
  const [filteredEmails, setFilteredEmails] = useState<EmailData[]>(emails);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredEmails?.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredEmails, page, rowsPerPage]);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<SnackbarInterface>({
    open: false,
    message: "",
    severity: "success",
  });
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingData, setEditingData] = useState<EmailsData>({
    email: "",
    mobile_num: "",
    subject: "",
    query: "",
    response: "",
    course_name: "",
  });

  const handleEditClick = (data: EmailsData) => {
    setEditingData({ ...data });
    setEditDialogOpen(true);
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setEditingData({
      email: "",
      mobile_num: "",
      subject: "",
      query: "",
      response: "",
      course_name: "",
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    applyFilter(e.target.value);
  };

  const loadQueries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await fetchUserQueries(
        process.env.REACT_APP_EMAIL_USER_ID ?? ""
      );
      const formattedQueries = data?.response?.respond_queries?.map(
        (query: {
          query_hash: string;
          query: string;
          response: string;
          email: string;
          mobile_num: string;
        }) => ({
          queryHash: query?.query_hash,
          query: query?.query,
          response: query?.response,
          email: query?.email,
          mobile_num: query?.mobile_num,
        })
      );
      dispatch(addEmail(formattedQueries));
      setFilteredEmails(formattedQueries);
    } catch (error) {
      console.error(EXPLORE_FILES?.FILE_LOAD_FAIL, error);
      setSnackbar({
        open: true,
        message: EXPLORE_FILES?.FILE_LOAD_FAIL,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  const applyFilter = useCallback(
    (filterValue: string) => {
      const lowerCaseFilter = filterValue?.toLowerCase();
      const filtered: EmailData[] = emails?.filter((email) =>
        Object.values(email)?.some(
          (value) =>
            typeof value === "string" &&
            value?.toLowerCase()?.includes(lowerCaseFilter)
        )
      );

      setFilteredEmails(filtered);
    },
    [emails]
  );

  const handleEditDialogSave = async () => {
    if (editingData) {
      if (
        !editingData?.subject ||
        !editingData?.query ||
        !editingData?.response
      ) {
        setSnackbar({
          open: true,
          message: "Please fill in all fields.",
          severity: "error",
        });
        return;
      }

      setIsLoading(true);
      try {
        const result = await sendEmail({
          email: editingData?.email,
          subject: editingData?.subject,
          response: editingData?.response,
          query: editingData?.query,
        });

        if (result) {
          setSnackbar({
            open: true,
            message: "Email sent successfully!",
            severity: "success",
          });
          setEditDialogOpen(false);
          await loadQueries();
          await handleSubmitEdit();
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: "Failed to send email.",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSubmitEdit = async () => {
    setIsLoading(true);

    try {
      const payload = {
        question: editingData?.query,
        edit_response: editingData?.response,
        admin_username: sessionStorage.getItem("user_display_name") || "admin",
        course_name: editingData?.course_name,
      };

      await editResponse(payload);
      setSnackbar({
        open: true,
        message: EXPLORE_FILES?.QUERY_UPDATED,
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating query:", error);
      setSnackbar({
        open: true,
        message: EXPLORE_FILES?.UPDATE_ERROR,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "Email",
      field: "email",
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      editable: false,
      width: 250,
    },
    {
      headerName: "Mobile Number",
      field: "mobile_num",
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      editable: false,
      width: 250,
    },
    {
      headerName: "Query",
      field: "query",
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      width: 250,
    },
    {
      headerName: "Response",
      field: "response",
      sortable: true,
      filter: true,
      headerComponent: CustomHeader,
      width: 300,
    },
    {
      headerName: "Actions",
      field: "actions",
      resizable: false,
      width: 200,
      cellRenderer: "actionCellRenderer",
    },
  ];

  const gridOptions: GridOptions<EmailData> = {
    columnDefs,
    rowData: paginatedData,
    defaultColDef: {
      flex: 1,
      minWidth: 100,
      filter: true,
      sortable: true,
      resizable: true,
    },
    components: {
      actionCellRenderer: EmailActionCellRenderer,
    },
    context: {
      onEdit: (data: EmailsData) => handleEditClick(data),
      classes,
    },
    pagination: true,
    paginationPageSize: rowsPerPage,
    rowModelType: "clientSide",
    suppressCellFocus: true,
    onGridReady: (params: GridReadyEvent) => {
      params?.api?.sizeColumnsToFit();
    },
  };

  const onGridReady = useCallback((params: GridReadyEvent) => {
    params?.api?.sizeColumnsToFit();
  }, []);

  useEffect(() => {
    loadQueries();
  }, [loadQueries]);

  return (
    <div className={classes?.container}>
      {isLoading && (
        <>
          <div className={classes?.overlay}></div>
          <div className={classes?.loader}>
            <CircularProgress size={60} />
          </div>
        </>
      )}
      <TextField
        value={searchTerm}
        onChange={handleSearch}
        placeholder="Search"
        variant="outlined"
        size="small"
        className={classes?.searchField}
      />
      <div className={`ag-theme-alpine ${classes?.agGridContainer}`}>
        <AgGridReact
          {...gridOptions}
          rowData={paginatedData}
          columnDefs={columnDefs}
          modules={[ClientSideRowModelModule]}
          className={`ag-theme-alpine ${classes?.agGridContainer}`}
          onGridReady={onGridReady}
          defaultColDef={{
            sortable: true,
            filter: true,
            resizable: true,
          }}
        />
        <TablePagination
          count={filteredEmails?.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 30, 40]}
          size="small"
          className={classes?.pagination}
        />
      </div>
      <Dialog
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        className={classes?.editDialog}
      >
        <DialogTitle>{EMAILS_CONSTANTS?.sendMailText}</DialogTitle>
        <DialogContent className={classes?.editDialogContent}>
          <TextField
            fullWidth
            label="Email"
            value={editingData?.email || ""}
            InputProps={{ readOnly: true }}
            className={classes?.dialogTextField}
          />
          <TextField
            fullWidth
            label="Phone Number"
            value={editingData?.mobile_num || ""}
            InputProps={{ readOnly: true }}
            className={classes?.dialogTextField}
          />
          <TextField
            fullWidth
            label="Subject"
            value={editingData?.subject || ""}
            onChange={(e) =>
              setEditingData((prev) =>
                prev ? { ...prev, subject: e.target.value } : prev
              )
            }
            className={classes?.dialogTextField}
          />
          <TextField
            fullWidth
            label="Query"
            value={editingData?.query || ""}
            multiline
            rows={4}
            InputProps={{ readOnly: true }}
            onChange={(e) =>
              setEditingData((prev) =>
                prev ? { ...prev, query: e.target.value } : prev
              )
            }
            className={classes?.dialogTextField}
          />
          <TextField
            fullWidth
            label="Response"
            value={editingData?.response || ""}
            multiline
            rows={4}
            onChange={(e) =>
              setEditingData((prev) =>
                prev ? { ...prev, response: e.target.value } : prev
              )
            }
            className={classes?.dialogTextField}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditDialogClose}>
            {EXPLORE_FILES?.CANCEL}
          </Button>
          <Button
            onClick={handleEditDialogSave}
            variant="contained"
            color="primary"
            disabled={isLoading}
          >
            {isLoading ? "Sending..." : "Send"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar?.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar?.severity}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EmailGrid;
